import React, { useEffect, useMemo, useRef, useState } from "react";
import GoogleMap from "google-maps-react-markers";
import { useSelector } from "react-redux";

import "./index.scss";

/** Native Map — this module exports `Map` (React), which shadows the global `Map` constructor for bare `new Map()`. */
const BucketsMap = window.Map;

const CLUSTER_ZOOM_THRESHOLD = 9;

/** Max tag-colored pills before collapsing remaining tech counts into "+N more". */
const MAX_VISIBLE_TAG_PILLS = 2;

const TAG_SORT_ORDER = [
  "certified_available",
  "certified_next_available",
  "certified_booked",
  "not_certified",
  "default",
];

function sortTagEntries(byTag) {
  return Object.entries(byTag).sort(([a], [b]) => {
    const ia = TAG_SORT_ORDER.indexOf(a);
    const ib = TAG_SORT_ORDER.indexOf(b);
    return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib);
  });
}

/** At lower zoom levels, bucket nearby pins and show per-tag counts with tag colors. */
function aggregateForZoom(data, zoom) {
  if (!Array.isArray(data) || data.length === 0) return [];

  if (zoom >= CLUSTER_ZOOM_THRESHOLD) {
    return data.map((el, idx) => ({
      type: "single",
      el,
      key: `single-${el?.id ?? idx}-${idx}`,
    }));
  }

  const decimals = zoom <= 5 ? 1 : zoom <= 7 ? 2 : 3;
  const factor = 10 ** decimals;
  const round = (n) => Math.round(n * factor) / factor;

  const buckets = new BucketsMap();
  for (let idx = 0; idx < data.length; idx++) {
    const el = data[idx];
    const lat = parseFloat(el?.lat);
    const lon = parseFloat(el?.lon);
    if (Number.isNaN(lat) || Number.isNaN(lon)) continue;

    const key = `${round(lat)},${round(lon)}`;
    if (!buckets.has(key)) {
      buckets.set(key, { latSum: 0, lonSum: 0, items: [] });
    }
    const b = buckets.get(key);
    b.latSum += lat;
    b.lonSum += lon;
    b.items.push(el);
  }

  let clusterIdx = 0;
  const results = [];
  for (const b of buckets.values()) {
    const n = b.items.length;
    const lat = b.latSum / n;
    const lon = b.lonSum / n;

    if (n === 1) {
      const el = b.items[0];
      results.push({
        type: "single",
        el,
        key: `single-${el?.id ?? clusterIdx}`,
      });
    } else {
      const byTag = {};
      for (const item of b.items) {
        const tag = item?.tag || "default";
        byTag[tag] = (byTag[tag] || 0) + 1;
      }
      results.push({
        type: "cluster",
        lat,
        lon,
        byTag,
        key: `cluster-${lat}-${lon}-${clusterIdx}`,
      });
    }
    clusterIdx += 1;
  }
  return results;
}

const AnyReactComponent = ({ tag }) => (
  <div className={`marker ${tag || ""}`}>
    <span></span>
  </div>
);

const JobLocationMarker = () => (
  <div className="map-job-marker">
    <img
      src="/assets/icons/job-location-icon.svg"
      alt=""
      width={33}
      height={51}
      draggable={false}
    />
  </div>
);

const ClusterMarker = ({ byTag }) => {
  const sorted = sortTagEntries(byTag);
  const visible = sorted.slice(0, MAX_VISIBLE_TAG_PILLS);
  const rest = sorted.slice(MAX_VISIBLE_TAG_PILLS);
  const moreCount = rest.reduce((sum, [, c]) => sum + c, 0);
  const title = sorted.map(([t, c]) => `${t}: ${c}`).join(" · ");

  return (
    <div className="marker-cluster" title={title}>
      {visible.map(([tag, count]) => (
        <div key={tag} className={`marker-cluster__count ${tag}`}>
          {count}
        </div>
      ))}
      {moreCount > 0 && (
        <div className="marker-cluster__more">
          +{moreCount} more
        </div>
      )}
    </div>
  );
};

function isValidLatLng(lat, lon) {
  const la = parseFloat(lat);
  const lo = parseFloat(lon);
  return !Number.isNaN(la) && !Number.isNaN(lo);
}

export const Map = ({ height, data, zoom = 4, search, jobLocation }) => {
  const accountData = useSelector(
    (state) => state.rootReducer.techsbookSlice.userData
  );
  const [dynamicZoom, setDynamicZoom] = useState(zoom);
  const [mapZoom, setMapZoom] = useState(Number(zoom) || 4);
  const [mapCenter, setMapCenter] = useState({
    lat: 39.116386,
    lng: -101.299591,
  });

  const mapRef = useRef(null);

  const markersToRender = useMemo(
    () => aggregateForZoom(data, mapZoom),
    [data, mapZoom]
  );

  const jobPin =
    jobLocation &&
    isValidLatLng(jobLocation.lat, jobLocation.lon)
      ? {
          lat: parseFloat(jobLocation.lat),
          lng: parseFloat(jobLocation.lon),
        }
      : null;

  useEffect(() => {
    setDynamicZoom(zoom);
    setMapZoom(Number(zoom) || 4);
  }, [zoom]);

  useEffect(() => {
    if (search?.lat) {
      setMapCenter({
        lat: search?.lat,
        lng: search?.lon,
      });
      mapRef?.current?.setCenter({
        lat: search?.lat,
        lng: search?.lon,
      });
    } else {
      if (accountData && Object.keys(accountData)?.length) {
        setMapCenter({
          lat: +accountData?.country_lat,
          lng: +accountData?.country_lon
        });
        mapRef?.current?.setCenter({
          lat: +accountData?.country_lat,
          lng: +accountData?.country_lon
        });
      } else {
        setMapCenter({ lat: 39.8283, lng: -98.5795 });
        mapRef?.current?.setCenter({ lat: 39.8283, lng: -98.5795 });
      }

    }
  }, [search, accountData]);

  const onGoogleApiLoaded = ({ map }) => {
    mapRef.current = map;
    setMapZoom(map.getZoom());
    map.addListener("zoom_changed", () => {
      setMapZoom(map.getZoom());
    });
  };

  return (
    <>
      <GoogleMap
        key={dynamicZoom}
        apiKey="AIzaSyBL99kJ-59bryhoVvhKkV6a2JlEFZhQG2I"
        defaultCenter={mapCenter}
        defaultZoom={Number(dynamicZoom)}
        mapMinHeight={height}
        onGoogleApiLoaded={onGoogleApiLoaded}
        options={{
          zoomControl: false,
          mapTypeControl: false,
          streetViewControl: false,
        }}
      >
        {markersToRender?.map((item) =>
          item.type === "single" ? (
            <AnyReactComponent
              key={item.key}
              tag={item.el?.tag}
              lat={parseFloat(item.el?.lat)}
              lng={parseFloat(item.el?.lon)}
            />
          ) : (
            <ClusterMarker
              key={item.key}
              lat={item.lat}
              lng={item.lon}
              byTag={item.byTag}
            />
          )
        )}
        {jobPin && (
          <JobLocationMarker
            lat={jobPin.lat}
            lng={jobPin.lng}
            zIndex={1000}
          />
        )}
      </GoogleMap>
    </>
  );
};
